import { Resource, Suggestion, epochISOString } from 'idea-toolbox';

import { EventSpotAttached } from './eventSpot.model';
import { Speaker } from './speaker.model';
import { ApprovedType, MealTypes, MEAL_CATEGORY_ASSIGNMENTS } from './meal.model';

type LegacyMealPreferenceType =
  | 'NO_PREFERENCE'
  | 'VEGAN'
  | 'VEGETARIAN'
  | 'PESCATARIAN'
  | 'NO_FISH'
  | 'NO_PORK'
  | 'HALAL'
  | 'GLUTEN_FREE'
  | 'LACTOSE_FREE'
  | 'NUT_FREE'
  | 'OTHER';

const LEGACY_MEAL_ASSIGNMENTS: Record<LegacyMealPreferenceType, MealTypes> = {
  NO_PREFERENCE: MealTypes.MEAL_1,
  VEGAN: MealTypes.SPECIAL,
  VEGETARIAN: MealTypes.MEAL_3,
  PESCATARIAN: MealTypes.MEAL_3,
  NO_FISH: MealTypes.MEAL_1,
  NO_PORK: MealTypes.MEAL_2,
  HALAL: MealTypes.MEAL_3,
  GLUTEN_FREE: MealTypes.MEAL_2,
  LACTOSE_FREE: MealTypes.SPECIAL,
  NUT_FREE: MealTypes.MEAL_1,
  OTHER: MealTypes.SPECIAL
};

export class User extends Resource {
  /**
   * The ID of the user; it's the concatenation of the key of the auth service and the username there.
   */
  userId: string;
  /**
   * Timestamp of when the user signed-up.
   */
  createdAt: epochISOString;
  /**
   * Timestamp of the last time the user did something.
   */
  lastSeenAt: epochISOString;
  /**
   * The service used to authenticate.
   */
  authService: AuthServices;
  /**
   * The first name of the user.
   */
  firstName: string;
  /**
   * The last name of the user.
   */
  lastName: string;
  /**
   * Email address for notifications (!== account email address).
   * Note: if bounced, it will finish in the black list for a while (see `sesNotifications.ts`).
   */
  email: string;
  /**
   * The URL to the user's avatar.
   */
  avatarURL: string;

  /**
   * ESN section code in ESN Accounts.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionCode?: string;
  /**
   * ESN country.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionCountry?: string;
  /**
   * ESN section name.
   * Set only for `AuthService.ESN_ACCOUNTS`.
   */
  sectionName?: string;
  /**
   * The user's birth date.
   */
  birthDate: epochISOString;
  /**
   * The number of the ESNcard. Must be valid to register for ESNers.
   * Set only for `AuthService.ESN_ACCOUNTS`
   */
  ESNcard?: string;
  /**
   * Timestamp of when the user's ESNcard was manually validated at check-in.
   */
  ESNcardValidatedAt?: epochISOString;
  /**
   * User ID of the staff member who validated the ESNcard at check-in.
   */
  ESNcardValidatedBy?: string;
  /**
   * How the ESNcard was validated at check-in.
   */
  ESNcardValidationMethod?: ESNcardValidationMethod;
  /**
   * Timestamp of when the user was checked in by the OC.
   */
  checkedInAt?: epochISOString;
  /**
   * User ID of the staff member who checked the user in.
   */
  checkedInBy?: string;
  /**
   * The permissions of the user on the app.
   */
  permissions: UserPermissions;
  /**
   * A custom block containing custom sections and fields for the registration form.
   */
  registrationForm: Record<string, any>;
  /**
   * The timestamp when the registration was submitted (if it was).
   * If set, the registration is considered completed.
   */
  registrationAt?: epochISOString;
  /**
   * The spot assigned for the event, if any.
   */
  spot?: EventSpotAttached;
  /**
   * The user's social media links, if any.
   */
  socialMedia: SocialMedia;
  /**
   * The list of contests (IDs) the user voted in.
   */
  votedInContests: string[];
  // @todo add this to registration field.
  /**
   * The user's meal preference.
   */
  mealType: MealTypes;
  /**
   * The user's meal tickets and their status.
   */
  mealTickets: { [mealId: string]: MealTicket };
  /**
   * Whether the user is part of the ESN International.
   */
  isESNInternational: boolean;

  load(x: any): void {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.createdAt = this.clean(x.createdAt, t => new Date(t).toISOString(), new Date().toISOString());
    this.lastSeenAt = new Date().toISOString();
    this.authService = this.clean(x.authService, String);
    this.firstName = this.clean(x.firstName, String);
    this.lastName = this.clean(x.lastName, String);
    this.email = this.clean(x.email, String);
    this.avatarURL = this.clean(x.avatarURL, String);
    this.birthDate = this.clean(x.birthDate, t => new Date(t).toISOString(), new Date().toISOString());

    if (this.authService === AuthServices.ESN_ACCOUNTS) {
      this.sectionCode = this.clean(x.sectionCode, String);
      this.sectionCountry = this.clean(x.sectionCountry, String);
      this.sectionName = this.clean(x.sectionName, String);
      this.ESNcard = this.clean(x.ESNcard, String);
      if (x.ESNcardValidatedAt)
        this.ESNcardValidatedAt = this.clean(x.ESNcardValidatedAt, t => new Date(t).toISOString());
      if (x.ESNcardValidatedBy) this.ESNcardValidatedBy = this.clean(x.ESNcardValidatedBy, String);
      if (x.ESNcardValidationMethod)
        this.ESNcardValidationMethod = this.clean(x.ESNcardValidationMethod, String);
      this.isESNInternational = this.clean(x.isESNInternational, Boolean, false);
    }
    if (x.checkedInAt) this.checkedInAt = this.clean(x.checkedInAt, t => new Date(t).toISOString());
    if (x.checkedInBy) this.checkedInBy = this.clean(x.checkedInBy, String);

    this.permissions = new UserPermissions(x.permissions);

    this.registrationForm = x.registrationForm ?? {};
    if (x.registrationAt) this.registrationAt = this.clean(x.registrationAt, t => new Date(t).toISOString());
    if (x.spot) this.spot = new EventSpotAttached(x.spot);
    this.socialMedia = {};
    if (x.socialMedia?.instagram) this.socialMedia.instagram = this.clean(x.socialMedia.instagram, String);
    if (x.socialMedia?.linkedIn) this.socialMedia.linkedIn = this.clean(x.socialMedia.linkedIn, String);
    if (x.socialMedia?.twitter) this.socialMedia.twitter = this.clean(x.socialMedia.twitter, String);

    this.votedInContests = this.cleanArray(x.votedInContests, String);

    this.mealType = this.normalizeStoredMealType(x.mealType);
    this.refreshMealData();
    if (!x.mealTickets) this.mealTickets = {};
    else this.mealTickets = x.mealTickets;
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.userId = safeData.userId;
    this.createdAt = safeData.createdAt;
    this.lastSeenAt = new Date().toISOString();
    this.authService = safeData.authService;

    if (this.authService === AuthServices.ESN_ACCOUNTS) {
      this.sectionCode = safeData.sectionCode;
      this.sectionCountry = safeData.sectionCountry;
      this.sectionName = safeData.sectionName;
      this.birthDate = safeData.birthDate;
      this.ESNcardValidatedAt = safeData.ESNcardValidatedAt;
      this.ESNcardValidatedBy = safeData.ESNcardValidatedBy;
      this.ESNcardValidationMethod = safeData.ESNcardValidationMethod;
      this.isESNInternational = safeData.isESNInternational;
    }
    this.checkedInAt = safeData.checkedInAt;
    this.checkedInBy = safeData.checkedInBy;

    this.permissions = safeData.permissions;

    if (safeData.registrationForm) this.registrationForm = safeData.registrationForm;
    if (safeData.registrationAt) this.registrationAt = safeData.registrationAt;
    if (safeData.spot) this.spot = safeData.spot;

    this.votedInContests = safeData.votedInContests;
    this.mealType = this.normalizeStoredMealType(safeData.mealType);
    this.refreshMealData();
  }

  validate(checkBirth = true): string[] {
    const e = super.validate();
    if (this.iE(this.firstName)) e.push('firstName');
    if (this.iE(this.lastName)) e.push('lastName');
    if (this.iE(this.email, 'email')) e.push('email');
    if (checkBirth &&
        new Date().getFullYear() - new Date(this.birthDate).getFullYear() < 18)
      e.push('birthDate');

    return e;
  }

  /**
   * Get the original user ID in the AuthService.
   */
  getAuthServiceUserId(): string {
    return this.userId.slice(this.authService.concat('_').length);
  }

  /**
   * Get the full name of the user.
   */
  getName(): string {
    return `${this.firstName} ${this.lastName}`;
  }

  /**
   * Get a string representing the ESN section and country of the user.
   */
  getSectionCountry(): string {
    return [this.sectionCountry, this.sectionName].filter(x => x).join(' - ');
  }

  /**
   * Whether the user is considered an external guest.
   */
  isExternal(): boolean {
    return this.authService !== AuthServices.ESN_ACCOUNTS;
  }

  isSpeaker(speaker: Speaker): boolean {
    return speaker.speakerId === this.userId;
  }

  isGalaxyInfoValid(): boolean {
    if (this.isExternal()) return true;
    return this.sectionName !== 'Unknown' && this.sectionCountry !== 'Unknown';
  }

  /**
   * Map the user into a Suggestion data structure.
   */
  mapIntoSuggestion(): Suggestion {
    return new Suggestion({
      value: this.userId,
      name: this.getName(),
      description: this.email,
      category1: this.sectionCountry,
      category2: this.sectionName
    });
  }

  isMealTicketApproved(mealId: string): boolean {
    return !!this.mealTickets?.[mealId]?.approvedAt;
  }

  get mealPreferenceLabels(): string[] {
    const dietaryRequirements = this.getRegistrationFormStrings('additional', 'DietaryRequirements');
    const normalizedLabels = dietaryRequirements
      .map(label => this.normalizeMealPreferenceLabel(label))
      .filter(Boolean);

    const cleanedRequirements =
      normalizedLabels.length > 1 ? normalizedLabels.filter(x => x !== 'No Preference') : normalizedLabels;
    return Array.from(new Set(cleanedRequirements.length ? cleanedRequirements.slice() : normalizedLabels.slice()));
  }

  get mealPreferenceSummary(): string {
    return this.mealPreferenceLabels.join(', ');
  }

  get mealMenuLabel(): string {
    return MEAL_CATEGORY_ASSIGNMENTS[this.mealType]?.menu ?? '';
  }

  get mealTypes(): MealTypes[] {
    return this.mealType ? [this.mealType] : [];
  }

  get mealCategorySummary(): string {
    return this.mealPreferenceSummary;
  }

  get mealCategorySummaryOrType(): string {
    return this.mealCategorySummary || this.mealType || '';
  }

  get additionalAllergenLabels(): string[] {
    const allergies = this.getExpandedAllergyLabels();
    const additionalRestrictions = this.getRegistrationFormString('additional', 'foodRestrictions');
    const labels = allergies.slice();

    if (additionalRestrictions) labels.push(additionalRestrictions);

    return Array.from(new Set(labels));
  }

  get additionalAllergensSummary(): string {
    return this.additionalAllergenLabels.join(', ');
  }

  get mealMenuSummary(): string {
    return this.mealMenuLabel;
  }

  get mealColorSummary(): string {
    return this.mealType ? MEAL_CATEGORY_ASSIGNMENTS[this.mealType]?.color ?? '' : '';
  }

  refreshMealData(): void {
    this.mealType = this.normalizeStoredMealType(this.mealType);
  }

  private getRegistrationFormStrings(sectionId: string, fieldId: string): string[] {
    const value = this.registrationForm?.[sectionId]?.[fieldId];

    if (!value) return [];
    if (Array.isArray(value)) return value.map(v => String(v).trim()).filter(Boolean);
    if (typeof value === 'string') return [value.trim()].filter(Boolean);
    if (typeof value === 'object')
      return Object.entries(value)
        .filter(([, selected]) => selected === true || selected === 'true' || selected === 1)
        .map(([option]) => option.trim())
        .filter(Boolean);

    return [];
  }

  private getRegistrationFormString(sectionId: string, fieldId: string): string {
    const value = this.registrationForm?.[sectionId]?.[fieldId];
    if (typeof value !== 'string') return '';
    return value.trim();
  }

  private getAssignedMealTypes(): MealTypes[] {
    return this.mealType ? [this.mealType] : [];
  }

  private getAssignedMealMenus(): Array<'MENU_1' | 'MENU_2' | 'MENU_3' | 'SPECIAL_MENU'> {
    const assignedMenu = this.mealType ? MEAL_CATEGORY_ASSIGNMENTS[this.mealType]?.menu : null;
    return assignedMenu ? [assignedMenu] : [];
  }

  private expandDietaryRequirements(requirement: string): string[] {
    return requirement
      .split(/[,;\n]+/)
      .map(part => part.trim())
      .filter(Boolean);
  }

  private getExpandedAllergyLabels(): string[] {
    return this.getRegistrationFormStrings('additional', 'Allergy')
      .reduce<string[]>((allergiesList, allergy) => allergiesList.concat(this.expandDietaryRequirements(allergy)), [])
      .map(allergy => allergy.trim())
      .filter(allergy => allergy && allergy !== 'None of Above' && allergy !== 'Other');
  }

  private mapDietaryRequirementToMealType(requirement: string): LegacyMealPreferenceType | undefined {
    switch (this.normalizeDietaryRequirement(requirement)) {
      case 'No Preference':
        return 'NO_PREFERENCE';
      case 'Vegan':
        return 'VEGAN';
      case 'Vegetarian':
        return 'VEGETARIAN';
      case 'Pescatarian':
        return 'PESCATARIAN';
      case 'No Pork':
        return 'NO_PORK';
      case 'No Fish':
        return 'NO_FISH';
      case 'Halal':
        return 'HALAL';
      case 'Gluten-free':
        return 'GLUTEN_FREE';
      case 'Lactose-free':
        return 'LACTOSE_FREE';
      case 'Nut-free':
        return 'NUT_FREE';
      case 'Other':
        return 'OTHER';
      default:
        return undefined;
    }
  }

  private normalizeStoredMealType(mealType?: string): MealTypes | undefined {
    if (!mealType) return undefined;
    if (Object.values(MealTypes).includes(mealType as MealTypes)) return mealType as MealTypes;
    if (mealType === 'MEAL_1') return MealTypes.MEAL_1;
    if (mealType === 'MEAL_2') return MealTypes.MEAL_2;
    if (mealType === 'MEAL_3') return MealTypes.MEAL_3;
    if (mealType === 'SPECIAL') return MealTypes.SPECIAL;
    return LEGACY_MEAL_ASSIGNMENTS[mealType as LegacyMealPreferenceType];
  }

  private normalizeDietaryRequirement(requirement: string): string {
    const normalizedRequirement = requirement
      ?.trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');

    switch (normalizedRequirement) {
      case 'no preference':
      case 'regular':
        return 'No Preference';
      case 'vegan':
        return 'Vegan';
      case 'vegetarian':
        return 'Vegetarian';
      case 'pescatarian':
        return 'Pescatarian';
      case 'no pork':
      case 'without pork':
        return 'No Pork';
      case 'no fish':
      case 'without fish':
        return 'No Fish';
      case 'halal':
        return 'Halal';
      case 'gluten free':
      case 'gluten-free':
      case 'gluten':
        return 'Gluten-free';
      case 'celery':
        return 'Celery';
      case 'cereals containing glutens':
      case 'cereals containing gluten':
        return 'Cereals containing glutens';
      case 'crustaceans':
        return 'Crustaceans';
      case 'eggs':
      case 'egg':
        return 'Eggs';
      case 'fish':
        return 'Fish';
      case 'seafood':
        return 'Seafood';
      case 'shellfish':
        return 'Shellfish';
      case 'lupin':
        return 'Lupin';
      case 'milk':
        return 'Milk';
      case 'dairy':
        return 'Dairy';
      case 'molluscs':
        return 'Molluscs';
      case 'mustard':
        return 'Mustard';
      case 'sesame seeds':
      case 'sesame':
        return 'Sesame seeds';
      case 'soya':
      case 'soy':
        return 'Soya';
      case 'sulphites':
      case 'sulfites':
        return 'Sulphites';
      case 'nuts':
      case 'nut':
        return 'Nuts';
      case 'peanuts':
      case 'peanut':
        return 'Peanuts';
      case 'lactose free':
      case 'lactose-free':
      case 'lactose':
        return 'Lactose-free';
      case 'nut free':
      case 'nut-free':
        return 'Nut-free';
      case 'other':
        return 'Other';
      default:
        return '';
    }
  }

  private normalizeMealPreferenceLabel(requirement: string): string {
    return this.normalizeDietaryRequirement(requirement) || requirement.trim();
  }

  hasConfirmedSpot(): boolean {
    return !!this.registrationAt && !!this.spot?.paymentConfirmedAt;
  }

  hasValidatedESNcard(): boolean {
    return !!this.ESNcardValidatedAt;
  }

  hasCheckedIn(): boolean {
    return !!this.checkedInAt;
  }
}

/**
 * The authentication services for the platform.
 */
export enum AuthServices {
  ESN_ACCOUNTS = 'EA', // aka Galaxy
  COGNITO = 'CO'
}

export enum ESNcardValidationMethod {
  SCAN = 'scan',
  MANUAL = 'manual'
}

/**
 * The permissions a user can have.
 */
export class UserPermissions {
  /**
   * Whether the user can manage spots assigned to their ESN country.
   */
  isCountryLeader: boolean;
  /**
   * Whether the user has administrative permissions on the registrations.
   */
  canManageRegistrations: boolean;
  /**
   * Whether the user havs administrative permissions on the contents (speakers, sessions, etc.).
   */
  canManageContents: boolean;
  /**
   * Whether the user has staff permissions.
   */
  isStaff: boolean;
  /**
   * Whether the user has maximum permissions over the app.
   * If this is true, all other permissions are.
   */
  protected _isAdmin: boolean;
  /**
   * Whether the user can manage spots for the ESN International delegation.
   */
  isESNInternationalLeader: boolean;

  get isAdmin(): boolean {
    return this._isAdmin;
  }
  set isAdmin(isAdmin: boolean) {
    this._isAdmin = isAdmin;
    if (isAdmin) {
      this.isCountryLeader = true;
      this.canManageRegistrations = true;
      this.canManageContents = true;
      this.isStaff = true;
      this.isESNInternationalLeader = true;
    }
  }

  constructor(x?: any) {
    x = x || {};
    this.isCountryLeader = Boolean(x.isCountryLeader);
    this.canManageRegistrations = Boolean(x.canManageRegistrations);
    this.canManageContents = Boolean(x.canManageContents);
    this.isStaff = Boolean(x.isStaff);
    this.isESNInternationalLeader = Boolean(x.isESNInternationalLeader);
    // as last, to change any other attribute in case it's `true`
    this.isAdmin = Boolean(x._isAdmin);
  }
}

export class UserFavoriteSession extends Resource {
  userId: string;
  sessionId: string;

  load(x: any): void {
    super.load(x);
    this.userId = this.clean(x.userId, String);
    this.sessionId = this.clean(x.sessionId, String);
  }
}

export interface SocialMedia {
  instagram?: string;
  linkedIn?: string;
  twitter?: string;
}

export interface MealTicket {
  approvedAt?: epochISOString;
  approvedBy?: string;
  approvedType?: ApprovedType;
}
